import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, UserPlus } from 'lucide-react';
import { createLead } from '@/api/crmCard';
import { getLeadSources, type PipelineItem } from '@/api/pipeline';
import { Sheet } from '@/components/Sheet';

interface Props {
  project: string;
  pipelines: PipelineItem[];
  defaultPipelineId: string;
  onCreated: (dealId: string) => void;
  onClose: () => void;
}

/**
 * «Новый лид» (кнопка «+» в «Воронке»): имя, телефон, источник, воронка → POST /api/crm/deals/lead.
 * Лид встаёт на «Новые» выбранной воронки; совпадения по телефону CRM склеивает сама.
 */
export function NewLeadSheet({ project, pipelines, defaultPipelineId, onCreated, onClose }: Props) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [source, setSource] = useState('');
  const [pipelineId, setPipelineId] = useState(defaultPipelineId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sources = useQuery({ queryKey: ['crm', 'lead-sources', project], queryFn: getLeadSources, staleTime: 10 * 60_000 });

  const canSend = name.trim().length > 0 && !busy;

  const submit = async () => {
    if (!canSend) return;
    setBusy(true);
    setError(null);
    try {
      const r = await createLead({ name: name.trim(), phone: phone.trim() || null, source: source || 'manual', pipelineId });
      onCreated(r.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось завести лид');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet label="Новый лид" onClose={onClose}>
      <div className="sh"><h3>Новый лид</h3></div>
      <form className="sb" style={{ gap: 12 }} onSubmit={(e) => { e.preventDefault(); void submit(); }}>
        <label className="ifield">
          <span>Имя или компания</span>
          <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" enterKeyHint="next" required />
        </label>
        <label className="ifield">
          <span>Телефон</span>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" inputMode="tel" autoComplete="tel" placeholder="+998 90 123-45-67" />
        </label>
        <label className="ifield">
          <span>Источник</span>
          <select value={source} onChange={(e) => setSource(e.target.value)} disabled={sources.isPending}>
            <option value="">{sources.isPending ? 'Загружаем…' : 'Вручную'}</option>
            {sources.data?.map((s) => <option key={s.id} value={s.key}>{s.name}</option>)}
          </select>
        </label>
        {pipelines.length > 1 && (
          <label className="ifield">
            <span>Воронка</span>
            <select value={pipelineId} onChange={(e) => setPipelineId(e.target.value)}>
              {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
        )}
        {error && <p className="pnote" role="alert" style={{ color: 'var(--danger)' }}>{error}</p>}
        <div className="btnrow">
          <button type="button" className="ghost" onClick={onClose}>Отмена</button>
          <button type="submit" className="primary" disabled={!canSend}>
            {busy ? <Loader2 className="animate-spin" aria-hidden /> : <UserPlus aria-hidden />}Завести
          </button>
        </div>
      </form>
    </Sheet>
  );
}
